import {
  ChatContentResultTypenames,
  ChatEventType,
  ChatSender,
  CustomerChatMessage,
  SendCustomerChatMessageResultTypenames,
  TrackEventType,
} from '@varo/graphql-schema-types';
import ChatTextField from 'features/chat/ChatTextField/ChatTextField';
import { getHistory } from 'features/chat/helpers/ChatEvent.helper';
import { useSendMessage } from 'features/chat/hooks/useSendMessage';
import useTrackEvent from 'features/chat/hooks/useTrackEvent';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, Pressable, StyleSheet, View, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import useDebounce from 'shared/hooks/useDebounce/useDebounce';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';

const MAX_CHARS = 200;
const EMPTY_STRING_REGEX = /^\s*$/;
export const TRACK_EVENT_DELAY = 10000;
const DECAGON_RESPONSE_TIMEOUT = 20000;

const handleStyle = (isButtonEnabled: boolean): ViewStyle => {
  if (isButtonEnabled) {
    return styles.enabledButton;
  }
  return styles.disabledButton;
};

const getIconColor = (
  isButtonEnabled: boolean,
  isAwaitingResponse: boolean,
) => {
  if (isButtonEnabled) {
    return theme.color.background.primary;
  }
  if (isAwaitingResponse) {
    return theme.color.interactive.disabled.border;
  }
  return theme.color.interactive.black;
};

const ChatInputBox = () => {
  const { bottom } = useSafeAreaInsets();
  const { isFeatureEnabled } = useStatsig();
  const isDecagonChatEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_DECAGON_CHAT,
  );
  const isDecagonChatSendBlockEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_DECAGON_CHAT_SEND_BLOCK,
  );
  const {
    isChatStarted,
    setHistory,
    chatSessionId,
    setIsAgentTyping,
    history,
    isChattingWithBot,
  } = useContext(ChatContext);
  const { t: translatedText } = useTranslation('chatStrings', {
    keyPrefix: 'inputBox',
  });
  const { sendCustomerMessage, sendMessageFetching, sendMessageResponse } =
    useSendMessage();

  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isAwaitingDecagonResponse, setIsAwaitingDecagonResponse] =
    useState(false);
  const decagonHistoryCountRef = useRef(0);

  const hasMessage = Boolean(message);
  const isError = Boolean(errorMessage);
  const isAwaitingResponse =
    isDecagonChatEnabled &&
    isAwaitingDecagonResponse &&
    isDecagonChatSendBlockEnabled &&
    isChattingWithBot;

  const isButtonEnabled =
    !isError && hasMessage && !sendMessageFetching && !isAwaitingResponse;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const { trackEvent } = useTrackEvent();
  const [isTyping, setIsTyping] = useState(false);

  const debouncedTrackEvent = useDebounce(() => {
    setIsTyping(false);
    trackEvent(TrackEventType.UserStoppedTyping);
  }, TRACK_EVENT_DELAY);

  const handleInputChange = (text: string) => {
    debouncedTrackEvent();
    if (text.length >= MAX_CHARS) {
      setErrorMessage(translatedText('maxCharactersError'));
    } else if (EMPTY_STRING_REGEX.test(text)) {
      setErrorMessage(translatedText('emptyMessageError'));
    } else {
      setErrorMessage('');
    }
    setIsTyping(true);
    setMessage(text);
  };

  const handleSubmit = async () => {
    const submittedMessage = message;
    try {
      setMessage('');
      setIsAgentTyping(true);

      // Capture bot message count before any state updates to avoid race conditions
      const currentBotCount = isDecagonChatEnabled
        ? history.filter((msg) => msg.sender === ChatSender.DecagonBot).length
        : 0;

      const messageEvent: CustomerChatMessage = {
        id: getUuid(),
        sender: ChatSender.User,
        createdAt: new Date().toJSON(),
        eventType: ChatEventType.Message,
        content: {
          message: submittedMessage,
          __typename: ChatContentResultTypenames.ChatContentMessage,
        },
        sessionId: chatSessionId,
      };
      setHistory((history) => getHistory(history, messageEvent));

      if (isDecagonChatEnabled) {
        decagonHistoryCountRef.current = currentBotCount;
        setIsAwaitingDecagonResponse(true);
      }

      await sendCustomerMessage(
        submittedMessage,
        ChatSender.User,
        isDecagonChatEnabled,
      );
      Keyboard.dismiss();
    } catch {
      setErrorMessage(translatedText('sendMessageError'));
      setIsAgentTyping(false);
      setIsAwaitingDecagonResponse(false);
    }
  };

  useEffect(() => {
    if (
      sendMessageResponse?.sendCustomerChatMessage.__typename ===
      SendCustomerChatMessageResultTypenames.SendCustomerChatMessageError
    ) {
      setErrorMessage(translatedText('sendMessageError'));
      setIsAgentTyping(false);
      setIsAwaitingDecagonResponse(false);
    }
  }, [sendMessageResponse, translatedText, setIsAgentTyping]);

  useEffect(() => {
    if (isAwaitingDecagonResponse) {
      const currentDecagonCount = history.filter(
        (msg) => msg.sender === ChatSender.DecagonBot,
      ).length;

      if (currentDecagonCount > decagonHistoryCountRef.current) {
        decagonHistoryCountRef.current = currentDecagonCount;
        setIsAwaitingDecagonResponse(false);
      }
    }
  }, [history, isAwaitingDecagonResponse]);

  useEffect(() => {
    if (!isAwaitingDecagonResponse || !isChattingWithBot) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsAwaitingDecagonResponse(false);
      setIsAgentTyping(false);
    }, DECAGON_RESPONSE_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [isAwaitingDecagonResponse, isChattingWithBot, setIsAgentTyping]);

  useEffect(() => {
    if (isTyping) {
      trackEvent(TrackEventType.UserStartedTyping);
    }
  }, [isTyping, trackEvent]);

  return (
    <View>
      <View style={[styles.footer, { paddingBottom: bottom }]} testID="footer">
        <View
          style={styles.input}
          pointerEvents={isAwaitingResponse ? 'none' : 'auto'}
        >
          <ChatTextField
            style={[
              styles.inputField,
              isError
                ? { backgroundColor: theme.color.background.tertiary }
                : null,
              isAwaitingResponse ? styles.inputFieldDisabled : null,
            ]}
            inputTextFieldLabel={null}
            placeholder={translatedText('placeholder')}
            editable={isChatStarted && !isAwaitingResponse}
            readOnly={isAwaitingResponse}
            scrollEnabled
            inputTextFieldAssistiveText={errorMessage}
            inputTextFieldError={isError}
            onChangeText={handleInputChange}
            value={isAwaitingResponse ? translatedText('responding') : message}
            assistiveTextIcon={
              <Icon
                iconKey="STATUS_ERROR"
                iconColor={theme.color.status.negative}
                iconSize="SM"
              />
            }
            maxLength={MAX_CHARS}
            aria-disabled={!isChatStarted}
            testID="chat-input-field"
          />
        </View>
        <Pressable
          style={[
            styles.icon,
            handleStyle(isButtonEnabled),
            isButtonEnabled && isHovered ? styles.hover : null,
            isButtonEnabled && isPressed ? styles.press : null,
            isButtonEnabled && isFocused ? styles.focus : null,
          ]}
          disabled={!isButtonEnabled}
          aria-disabled={!isButtonEnabled}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => {
            setIsHovered(false);
          }}
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          onPressIn={() => setIsPressed(true)}
          onPressOut={() => setIsPressed(false)}
          onPress={handleSubmit}
          testID="send-button"
        >
          <Icon
            iconKey="SEND"
            iconAccessibilityLabel="send-icon"
            iconColor={getIconColor(isButtonEnabled, isAwaitingResponse)}
          />
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  disabledButton: {
    backgroundColor: theme.color.background.secondary,
  },
  enabledButton: {
    backgroundColor: theme.color.interactive.primary,
  },
  focus: {
    backgroundColor: theme.color.interactive.primaryHovered,
    borderColor: theme.color.border.focused,
  },
  footer: {
    backgroundColor: theme.color.background.surface,
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
  hover: {
    backgroundColor: theme.color.interactive.primaryHovered,
  },
  icon: {
    alignItems: 'center',
    borderRadius: 22,
    display: 'flex',
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  input: {
    flex: 1,
  },
  inputField: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  inputFieldDisabled: {
    backgroundColor: theme.color.interactive.disabled.background,
  },
  press: {
    backgroundColor: theme.color.interactive.primaryPressed,
  },
});

export default ChatInputBox;
